<template>
  <div class="planner">
    <!-- ===== Header ===== -->
    <div class="pl-header">
      <div>
        <h1 class="pl-title">Financial planner</h1>
        <p class="pl-subtitle">Budgets, goals, forecasting and growth — for {{ company.name || 'your business' }}.</p>
      </div>
      <button type="button" class="refresh-btn" :disabled="loading" title="Refresh" aria-label="Refresh" @click="fetchAll">
        <CIcon name="cil-reload" :class="{ spin: loading }" />
      </button>
    </div>

    <!-- ===== KPI summary ===== -->
    <CRow class="kpi-row" :class="{ loading }">
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top"><span class="kpi-label">Goals progress</span><span class="kpi-chip chip-success"><CIcon name="cil-flag-alt" /></span></div>
          <div class="kpi-value">{{ goalsSummary.avgProgress }}%</div>
          <div class="kpi-sub">{{ goalsSummary.count }} goal{{ goalsSummary.count === 1 ? '' : 's' }} · {{ goalsSummary.completed }} done</div>
        </div>
      </CCol>
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top"><span class="kpi-label">Budget used</span><span class="kpi-chip chip-warning"><CIcon name="cil-chart-pie" /></span></div>
          <div class="kpi-value">{{ budgetTotals.usedPct }}%</div>
          <div class="kpi-sub">{{ currentYear }} · {{ formatCurrency(budgetTotals.actual) }} of {{ formatCurrency(budgetTotals.target) }}</div>
        </div>
      </CCol>
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top"><span class="kpi-label">Active projects</span><span class="kpi-chip chip-info"><CIcon name="cil-briefcase" /></span></div>
          <div class="kpi-value">{{ activeProjectCount }}</div>
          <div class="kpi-sub">{{ formatCurrency(projectBudgetTotal) }} total budget</div>
        </div>
      </CCol>
      <CCol sm="6" xl="3" class="mb-4">
        <div class="kpi-card">
          <div class="kpi-top"><span class="kpi-label">Net worth</span><span class="kpi-chip chip-navy"><CIcon name="cil-balance-scale" /></span></div>
          <div class="kpi-value" :class="netWorth < 0 ? 'text-danger-strong' : ''">{{ formatCurrency(netWorth) }}</div>
          <div class="kpi-sub">As of today · investments − debt</div>
        </div>
      </CCol>
    </CRow>

    <!-- ===== Budget vs Actual ===== -->
    <CCard class="pl-card mb-4">
      <CCardHeader class="pl-card-header">
        <div>
          <h5 class="pl-card-title mb-0">Budget vs actual</h5>
          <small class="text-muted">{{ currentYear }} — targets you set vs. real spend (from expenses)</small>
        </div>
        <CButton size="sm" class="btn-brand-accent" @click="openCreate('budget')"><CIcon name="cil-plus" /> Add budget</CButton>
      </CCardHeader>
      <CCardBody class="pt-2 pb-4 px-4">
        <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
        <div v-else-if="budgetRows.length === 0" class="pl-empty">
          <CIcon name="cil-chart-pie" size="2xl" class="text-muted mb-2" />
          <div>No budget targets yet. Add one to track spend against plan.</div>
        </div>
        <CRow v-else>
          <CCol md="5" class="d-flex align-items-center justify-content-center">
            <div style="height: 240px; width: 240px"><BudgetAllocationChart :chartData="budgetAllocationData" /></div>
          </CCol>
          <CCol md="7">
            <div class="table-responsive">
              <table class="table pl-table mb-0">
                <thead>
                  <tr><th>Category</th><th class="text-right">Budget</th><th class="text-right">Actual</th><th class="text-right">Variance</th><th class="text-right">Actions</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(b, i) in budgetRows" :key="b.id">
                    <td><span class="cat-dot" :style="{ backgroundColor: palette(i) }"></span>{{ b.category }}</td>
                    <td class="text-right">{{ formatCurrency(b.amount) }}</td>
                    <td class="text-right">{{ formatCurrency(b.actual) }}</td>
                    <td class="text-right" :class="b.variancePct <= 0 ? 'text-success-strong' : 'text-danger-strong'">
                      {{ b.variancePct > 0 ? '+' : '' }}{{ b.variancePct }}%
                    </td>
                    <td class="text-right text-nowrap">
                      <CButton size="sm" color="light" class="border mr-1" aria-label="Edit" @click="openEdit('budget', b)"><CIcon name="cil-pencil" /></CButton>
                      <CButton size="sm" color="light" class="border" aria-label="Delete" @click="confirmDelete('budget', b)"><CIcon name="cil-trash" /></CButton>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="pl-total">
                    <td>Total</td>
                    <td class="text-right">{{ formatCurrency(budgetTotals.target) }}</td>
                    <td class="text-right">{{ formatCurrency(budgetTotals.actual) }}</td>
                    <td class="text-right" :class="budgetTotals.variancePct <= 0 ? 'text-success-strong' : 'text-danger-strong'">
                      {{ budgetTotals.variancePct > 0 ? '+' : '' }}{{ budgetTotals.variancePct }}%
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- ===== Goals + Cash Flow Forecast ===== -->
    <CRow>
      <CCol lg="6">
        <CCard class="pl-card mb-4 h-100">
          <CCardHeader class="pl-card-header">
            <div><h5 class="pl-card-title mb-0">Financial goals</h5><small class="text-muted">Track your business objectives</small></div>
            <CButton size="sm" class="btn-brand-accent" @click="openCreate('goal')"><CIcon name="cil-plus" /> Add goal</CButton>
          </CCardHeader>
          <CCardBody class="pt-2 pb-3 px-4">
            <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="goals.length === 0" class="pl-empty"><CIcon name="cil-flag-alt" size="2xl" class="text-muted mb-2" /><div>No goals yet.</div></div>
            <div v-else>
              <div v-for="g in goals" :key="g.id" class="goal-row">
                <div class="d-flex justify-content-between align-items-start mb-1">
                  <div>
                    <div class="font-weight-semibold">{{ g.name }}</div>
                    <small class="text-muted">{{ g.category || '—' }}<span v-if="g.targetDate"> · due {{ formatDate(g.targetDate) }}</span></small>
                  </div>
                  <div class="d-flex align-items-center">
                    <CBadge :color="goalProgress(g) >= 75 ? 'success' : goalProgress(g) >= 25 ? 'info' : 'warning'" class="mr-2">{{ goalProgress(g) }}%</CBadge>
                    <CButton size="sm" color="light" class="border mr-1" aria-label="Edit" @click="openEdit('goal', g)"><CIcon name="cil-pencil" /></CButton>
                    <CButton size="sm" color="light" class="border" aria-label="Delete" @click="confirmDelete('goal', g)"><CIcon name="cil-trash" /></CButton>
                  </div>
                </div>
                <div class="d-flex justify-content-between small text-muted mb-1">
                  <span>{{ formatCurrency(g.currentAmount) }}</span><span>{{ formatCurrency(g.targetAmount) }}</span>
                </div>
                <CProgress :value="goalProgress(g)" height="8px" :color="goalProgress(g) >= 75 ? 'success' : goalProgress(g) >= 25 ? 'info' : 'warning'" />
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="6">
        <CCard class="pl-card mb-4 h-100">
          <CCardHeader class="pl-card-header">
            <div><h5 class="pl-card-title mb-0">Cash flow forecast</h5><small class="text-muted">Next 6 months — from receivables, payables &amp; run-rate</small></div>
          </CCardHeader>
          <CCardBody class="pt-2 pb-4 px-4">
            <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
            <ExtendedCashFlowChart v-else :chartData="cashFlowForecastData" style="height: 230px" />
            <div class="row mt-3">
              <div class="col-6">
                <div class="small text-muted">Projected Inflows</div>
                <div class="font-weight-bold text-success-strong">{{ formatCurrency(forecast.totalIn) }}</div>
              </div>
              <div class="col-6 text-right">
                <div class="small text-muted">Projected Outflows</div>
                <div class="font-weight-bold text-danger-strong">{{ formatCurrency(forecast.totalOut) }}</div>
              </div>
            </div>
            <hr class="my-3" />
            <div class="row">
              <div class="col-6">
                <div class="small text-muted">Current Balance</div>
                <div class="font-weight-bold">{{ formatCurrency(forecast.startBalance) }}</div>
              </div>
              <div class="col-6 text-right">
                <div class="small text-muted">Projected (6mo)</div>
                <div class="font-weight-bold" :class="forecast.endBalance >= forecast.startBalance ? 'text-success-strong' : 'text-danger-strong'">{{ formatCurrency(forecast.endBalance) }}</div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ===== Business Growth ===== -->
    <CCard class="pl-card mb-4">
      <CCardHeader class="pl-card-header">
        <div><h5 class="pl-card-title mb-0">Business growth</h5><small class="text-muted">Revenue, expenses &amp; profit by quarter (from invoices &amp; expenses)</small></div>
      </CCardHeader>
      <CCardBody class="pt-2 pb-4 px-4">
        <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
        <BusinessGrowthChart v-else :chartData="businessGrowthData" style="height: 300px" />
        <CRow class="mt-4">
          <CCol md="4">
            <div class="metric metric-info"><div class="metric-label">Revenue Growth (YoY)</div><div class="metric-value">{{ growthMetrics.revenueGrowth }}%</div></div>
          </CCol>
          <CCol md="4">
            <div class="metric metric-success"><div class="metric-label">Profit Margin</div><div class="metric-value">{{ growthMetrics.profitMargin }}%</div></div>
          </CCol>
          <CCol md="4">
            <div class="metric metric-navy"><div class="metric-label">Avg Monthly Profit</div><div class="metric-value">{{ formatCurrency(growthMetrics.avgMonthlyProfit) }}</div></div>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- ===== Tax Planning + Projects ===== -->
    <CRow>
      <CCol lg="5">
        <CCard class="pl-card mb-4 h-100">
          <CCardHeader class="pl-card-header">
            <div><h5 class="pl-card-title mb-0">Tax planning</h5><small class="text-muted">{{ currentYear }} tax calendar</small></div>
            <CButton size="sm" class="btn-brand-outline" @click="navigateTo('/tenants/tax/dashboard')"><CIcon name="cil-calculator" /> Tax</CButton>
          </CCardHeader>
          <CCardBody class="pt-2 pb-3 px-4">
            <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="taxEvents.length === 0" class="pl-empty"><CIcon name="cil-calendar" size="2xl" class="text-muted mb-2" /><div>No tax periods found.</div></div>
            <div v-else>
              <div v-for="t in taxEvents" :key="t.id" class="tax-row">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="font-weight-semibold">{{ t.name }}</div>
                    <small class="text-muted">Due {{ formatDate(t.dueDate) }}</small>
                  </div>
                  <div class="text-right">
                    <CBadge :color="deadlineColor(t.dueDate)">{{ daysText(t.dueDate) }}</CBadge>
                    <div v-if="t.amount" class="small mt-1">{{ formatCurrency(t.amount) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </CCardBody>
          <CCardFooter class="pl-card-footer d-flex justify-content-between align-items-center">
            <div><div class="font-weight-semibold">Estimated Tax Payable</div><small class="text-muted">From open tax periods</small></div>
            <div class="h5 mb-0">{{ formatCurrency(taxSummary.estimated) }}</div>
          </CCardFooter>
        </CCard>
      </CCol>

      <CCol lg="7">
        <CCard class="pl-card mb-4 h-100">
          <CCardHeader class="pl-card-header">
            <div><h5 class="pl-card-title mb-0">Projects</h5><small class="text-muted">Budget tracking</small></div>
            <CButton size="sm" class="btn-brand-accent" @click="openCreate('project')"><CIcon name="cil-plus" /> New project</CButton>
          </CCardHeader>
          <CCardBody class="pt-2 pb-3 px-0">
            <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="projects.length === 0" class="pl-empty"><CIcon name="cil-briefcase" size="2xl" class="text-muted mb-2" /><div>No projects yet.</div></div>
            <div v-else class="table-responsive">
              <table class="table pl-table mb-0">
                <thead><tr><th class="pl-4">Project</th><th>Budget / Spent</th><th>Progress</th><th class="text-right pr-4">Actions</th></tr></thead>
                <tbody>
                  <tr v-for="p in projects" :key="p.id">
                    <td class="pl-4"><div class="font-weight-semibold">{{ p.name }}</div><small class="text-muted">{{ p.manager || '—' }} · {{ p.statusDescription || statusLabel('project', p.status) }}</small></td>
                    <td>
                      <div class="small">{{ formatCurrency(p.spent) }} / {{ formatCurrency(p.budget) }}</div>
                      <CProgress :value="pct(p.spent, p.budget)" height="4px" :color="pct(p.spent, p.budget) > 100 ? 'danger' : 'success'" />
                    </td>
                    <td><div class="small">{{ p.progress || 0 }}%</div><CProgress :value="p.progress || 0" height="4px" color="info" /></td>
                    <td class="text-right pr-4 text-nowrap">
                      <CButton size="sm" color="light" class="border mr-1" aria-label="Edit" @click="openEdit('project', p)"><CIcon name="cil-pencil" /></CButton>
                      <CButton size="sm" color="light" class="border" aria-label="Delete" @click="confirmDelete('project', p)"><CIcon name="cil-trash" /></CButton>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- ===== Debt + Investments ===== -->
    <CRow>
      <CCol lg="5">
        <CCard class="pl-card mb-4 h-100">
          <CCardHeader class="pl-card-header">
            <div><h5 class="pl-card-title mb-0">Debt management</h5><small class="text-muted">Loans &amp; repayment</small></div>
            <CButton size="sm" class="btn-brand-accent" @click="openCreate('loan')"><CIcon name="cil-plus" /> Add loan</CButton>
          </CCardHeader>
          <CCardBody class="pt-2 pb-3 px-4">
            <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="loans.length === 0" class="pl-empty"><CIcon name="cil-bank" size="2xl" class="text-muted mb-2" /><div>No loans tracked.</div></div>
            <div v-else>
              <div v-for="l in loans" :key="l.id" class="loan-row">
                <div class="d-flex justify-content-between align-items-start mb-1">
                  <div><div class="font-weight-semibold">{{ l.name }}</div><small class="text-muted">{{ l.lender || '—' }} · {{ l.loanType || '—' }}</small></div>
                  <div class="d-flex align-items-center">
                    <CBadge :color="loanColor(l.status)" class="mr-2">{{ l.statusDescription || statusLabel('loan', l.status) }}</CBadge>
                    <CButton size="sm" color="light" class="border mr-1" aria-label="Edit" @click="openEdit('loan', l)"><CIcon name="cil-pencil" /></CButton>
                    <CButton size="sm" color="light" class="border" aria-label="Delete" @click="confirmDelete('loan', l)"><CIcon name="cil-trash" /></CButton>
                  </div>
                </div>
                <div class="d-flex justify-content-between small text-muted mb-1">
                  <span>Paid {{ formatCurrency(l.amountPaid) }}</span><span>{{ formatCurrency(l.principal) }} @ {{ l.interestRate }}%</span>
                </div>
                <CProgress :value="pct(l.amountPaid, l.principal)" height="6px" color="success" />
              </div>
            </div>
          </CCardBody>
          <CCardFooter class="pl-card-footer">
            <div class="row">
              <div class="col-6"><div class="small text-muted">Total Debt</div><div class="font-weight-bold">{{ formatCurrency(debtSummary.totalDebt) }}</div></div>
              <div class="col-6 text-right"><div class="small text-muted">Monthly Payments</div><div class="font-weight-bold">{{ formatCurrency(debtSummary.monthlyPayments) }}</div></div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>

      <CCol lg="7">
        <CCard class="pl-card mb-4 h-100">
          <CCardHeader class="pl-card-header">
            <div><h5 class="pl-card-title mb-0">Investments</h5><small class="text-muted">Portfolio &amp; performance</small></div>
            <CButton size="sm" class="btn-brand-accent" @click="openCreate('investment')"><CIcon name="cil-plus" /> Add investment</CButton>
          </CCardHeader>
          <CCardBody class="pt-2 pb-3 px-4">
            <div v-if="loading" class="pl-placeholder"><CSpinner color="primary" /></div>
            <div v-else-if="investments.length === 0" class="pl-empty"><CIcon name="cil-chart" size="2xl" class="text-muted mb-2" /><div>No investments tracked.</div></div>
            <CRow v-else>
              <CCol md="5" class="d-flex align-items-center justify-content-center">
                <div style="height: 200px; width: 200px"><InvestmentAllocationChart :chartData="investmentAllocationData" /></div>
              </CCol>
              <CCol md="7">
                <div class="table-responsive">
                  <table class="table pl-table mb-0">
                    <thead><tr><th>Investment</th><th class="text-right">Amount</th><th class="text-right">Perf.</th><th class="text-right">Actions</th></tr></thead>
                    <tbody>
                      <tr v-for="(inv, i) in investments" :key="inv.id">
                        <td><span class="cat-dot" :style="{ backgroundColor: palette(i) }"></span>{{ inv.name }}<div class="small text-muted">{{ inv.investmentType || '—' }}</div></td>
                        <td class="text-right">{{ formatCurrency(inv.amount) }}</td>
                        <td class="text-right"><CBadge :color="inv.performance >= 0 ? 'success' : 'danger'">{{ inv.performance >= 0 ? '+' : '' }}{{ inv.performance }}%</CBadge></td>
                        <td class="text-right text-nowrap">
                          <CButton size="sm" color="light" class="border mr-1" aria-label="Edit" @click="openEdit('investment', inv)"><CIcon name="cil-pencil" /></CButton>
                          <CButton size="sm" color="light" class="border" aria-label="Delete" @click="confirmDelete('investment', inv)"><CIcon name="cil-trash" /></CButton>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
          <CCardFooter class="pl-card-footer">
            <div class="row">
              <div class="col-4"><div class="small text-muted">Total Invested</div><div class="font-weight-bold">{{ formatCurrency(investmentSummary.total) }}</div></div>
              <div class="col-4"><div class="small text-muted">Total Return</div><div class="font-weight-bold text-success-strong">{{ formatCurrency(investmentSummary.totalReturn) }}</div></div>
              <div class="col-4"><div class="small text-muted">Overall ROI</div><div class="font-weight-bold" :class="investmentSummary.roi >= 0 ? 'text-success-strong' : 'text-danger-strong'">{{ investmentSummary.roi >= 0 ? '+' : '' }}{{ investmentSummary.roi }}%</div></div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- ===== CRUD modal ===== -->
    <PlannerFormModal
      :show="modal.show"
      :title="modal.title"
      :fields="modal.fields"
      :item="modal.item"
      @close="modal.show = false"
      @save="onSave"
    />

    <!-- ===== Delete confirm ===== -->
    <CModal :title="`Delete this ${del.label}?`" :show.sync="del.show">
      <p class="mb-0">
        <strong v-if="del.item && (del.item.name || del.item.category)">{{ del.item.name || del.item.category }}</strong>
        will be removed from your planner. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="del.deleting" @click="del.show = false">Keep it</CButton>
        <CButton color="danger" :disabled="del.deleting" @click="doDelete">
          {{ del.deleting ? "Deleting…" : `Delete ${del.label}` }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { freeSet } from "@coreui/icons";
import { mytToday, asDateString, addDays, diffDays, formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import ExtendedCashFlowChart from "../widgets/ExtendedCashFlowChart";
import BusinessGrowthChart from "../widgets/BusinessGrowthChart";
import BudgetAllocationChart from "../widgets/BudgetAllocationChart";
import InvestmentAllocationChart from "../widgets/InvestmentAllocationChart";
import PlannerFormModal from "../components/PlannerFormModal.vue";

import ProfileApi from "@/lib/profileApi";
import FinancialGoalApi from "@/lib/financialGoalApi";
import ProjectApi from "@/lib/projectApi";
import LoanApi from "@/lib/loanApi";
import InvestmentApi from "@/lib/investmentApi";
import BudgetApi from "@/lib/budgetApi";
import InvoiceApi from "@/lib/invoiceApi";
import BillApi from "@/lib/billApi";
import ExpenseApi from "@/lib/expenseApi";

const BRAND = ["#1E3A5F", "#10B981", "#EF4444", "#F59E0B", "#3B82F6", "#94A3B8", "#2A4D7C", "#059669"];
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Inclusive "yyyy-MM-dd" range covering `count` months starting `offset` months from this month
// (Malaysia time). offset -1 = last month, +1 = next month.
function monthSpan(offset, count) {
  const [y, m] = mytToday().split("-").map(Number);
  const startIdx = y * 12 + (m - 1) + offset;
  const toStr = (idx) => `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}-01`;
  return {
    start: toStr(startIdx),
    end: addDays(toStr(startIdx + count), -1),
    year: Math.floor(startIdx / 12),
    month: (startIdx % 12) + 1,
  };
}

export default {
  name: "DashboardPlanner",
  freeSet,
  components: {
    ExtendedCashFlowChart,
    BusinessGrowthChart,
    BudgetAllocationChart,
    InvestmentAllocationChart,
    PlannerFormModal,
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      loading: false,
      company: {},

      profileApi: new ProfileApi(),
      goalApi: new FinancialGoalApi(),
      projectApi: new ProjectApi(),
      loanApi: new LoanApi(),
      investmentApi: new InvestmentApi(),
      budgetApi: new BudgetApi(),
      invoiceApi: new InvoiceApi(),
      billApi: new BillApi(),
      expenseApi: new ExpenseApi(),

      goals: [],
      projects: [],
      loans: [],
      investments: [],
      budgets: [],
      invoicesRaw: [],
      billsRaw: [],
      expensesRaw: [],
      taxPeriods: [],
      banks: [],

      modal: { show: false, title: "", fields: [], item: {}, entity: null },
      del: { show: false, entity: null, item: null, label: "", deleting: false },
    };
  },

  mounted() {
    this.$store.commit("setPageNavItems", ["Dashboard", "Planner"]);
    this.fetchAll();
  },

  computed: {
    businessCurrency() {
      const b = (typeof auth !== "undefined" && auth.getLocalBusiness && auth.getLocalBusiness()) || {};
      return this.company.currency || b.currency || "MYR";
    },

    // ---- Goals ----
    goalsSummary() {
      const count = this.goals.length;
      const completed = this.goals.filter((g) => this.goalProgress(g) >= 100 || g.status === 1).length;
      const avg = count ? Math.round(this.goals.reduce((s, g) => s + this.goalProgress(g), 0) / count) : 0;
      return { count, completed, avgProgress: avg };
    },

    // ---- Budget vs actual ----
    budgetRows() {
      return this.budgets
        .filter((b) => !b.year || b.year === this.currentYear)
        .map((b) => {
          const actual = this.expenseActualForCategory(b.category, b.year || this.currentYear);
          const target = Number(b.amount) || 0;
          const variancePct = target ? Math.round(((actual - target) / target) * 1000) / 10 : 0;
          return { ...b, actual, variancePct };
        });
    },
    budgetTotals() {
      const target = this.budgetRows.reduce((s, b) => s + (Number(b.amount) || 0), 0);
      const actual = this.budgetRows.reduce((s, b) => s + b.actual, 0);
      const variancePct = target ? Math.round(((actual - target) / target) * 1000) / 10 : 0;
      const usedPct = target ? Math.round((actual / target) * 100) : 0;
      return { target, actual, variancePct, usedPct };
    },
    budgetAllocationData() {
      return {
        labels: this.budgetRows.map((b) => b.category),
        datasets: [{ data: this.budgetRows.map((b) => Number(b.amount) || 0), backgroundColor: this.budgetRows.map((b, i) => this.palette(i)) }],
      };
    },

    // ---- Projects ----
    activeProjectCount() {
      return this.projects.filter((p) => p.status !== 4).length;
    },
    projectBudgetTotal() {
      return this.projects.reduce((s, p) => s + (Number(p.budget) || 0), 0);
    },

    // ---- Loans / debt ----
    debtSummary() {
      const totalDebt = this.loans.reduce((s, l) => s + Math.max(0, (Number(l.principal) || 0) - (Number(l.amountPaid) || 0)), 0);
      const monthlyPayments = this.loans.filter((l) => l.status === 0 || l.status === 3).reduce((s, l) => s + (Number(l.monthlyPayment) || 0), 0);
      return { totalDebt, monthlyPayments };
    },

    // ---- Investments ----
    investmentSummary() {
      const total = this.investments.reduce((s, i) => s + (Number(i.amount) || 0), 0);
      const totalReturn = this.investments.reduce((s, i) => s + (Number(i.returnAmount) || 0), 0);
      const roi = total ? Math.round((totalReturn / total) * 1000) / 10 : 0;
      return { total, totalReturn, roi };
    },
    investmentAllocationData() {
      return {
        labels: this.investments.map((i) => i.name),
        datasets: [{ data: this.investments.map((i) => Number(i.amount) || 0), backgroundColor: this.investments.map((i, idx) => this.palette(idx)) }],
      };
    },
    netWorth() {
      return this.investmentSummary.total - this.debtSummary.totalDebt;
    },

    // ---- Business growth (quarters) ----
    growthQuarters() {
      const arr = [];
      const thisMonth = Number(mytToday().substring(5, 7));
      const quarterStartOffset = -((thisMonth - 1) % 3); // months back to the start of this quarter
      for (let i = 3; i >= 0; i--) {
        const q = monthSpan(quarterStartOffset - 3 * i, 3);
        const start = q.start;
        const end = q.end;
        const revenue = this.invoicesRaw
          .filter((x) => x.statusDescription === "Approve" && this.inRange(x.issuedDate, start, end))
          .reduce((s, x) => s + this.invAmount(x), 0);
        const expenses = this.expensesRaw
          .filter((x) => this.expenseCounted(x) && this.inRange(x.date, start, end))
          .reduce((s, x) => s + (Number(x.totalAmount) || 0), 0) +
          this.billsRaw
            .filter((x) => x.stateDescription === "Approve" && this.inRange(x.date, start, end))
            .reduce((s, x) => s + (Number(x.totalAmount) || 0), 0);
        const quarterNo = Math.floor((q.month - 1) / 3) + 1;
        arr.push({ label: "Q" + quarterNo + " " + String(q.year).substring(2), revenue, expenses, profit: revenue - expenses });
      }
      return arr;
    },
    businessGrowthData() {
      const q = this.growthQuarters;
      return {
        labels: q.map((x) => x.label),
        datasets: [
          { label: "Revenue", backgroundColor: "#3B82F6", data: q.map((x) => x.revenue) },
          { label: "Expenses", backgroundColor: "#EF4444", data: q.map((x) => x.expenses) },
          { label: "Profit", backgroundColor: "#10B981", data: q.map((x) => x.profit) },
        ],
      };
    },
    growthMetrics() {
      const q = this.growthQuarters;
      const last = q[q.length - 1] || { revenue: 0, profit: 0 };
      const first = q[0] || { revenue: 0 };
      const revenueGrowth = first.revenue ? Math.round(((last.revenue - first.revenue) / first.revenue) * 1000) / 10 : 0;
      const totalRev = q.reduce((s, x) => s + x.revenue, 0);
      const totalProfit = q.reduce((s, x) => s + x.profit, 0);
      const profitMargin = totalRev ? Math.round((totalProfit / totalRev) * 1000) / 10 : 0;
      const avgMonthlyProfit = totalProfit / 12;
      return { revenueGrowth, profitMargin, avgMonthlyProfit };
    },

    // ---- Cash flow forecast (6 months) ----
    forecast() {
      const startBalance = this.banks.reduce((s, b) => s + (Number(b.balance) || 0), 0);
      const months = [];
      // Recurring monthly outflow = avg of last 3 months expenses
      const recent = [];
      for (let i = 1; i <= 3; i++) {
        const { start: s, end: e } = monthSpan(-i, 1);
        recent.push(this.expensesRaw.filter((x) => this.expenseCounted(x) && this.inRange(x.date, s, e)).reduce((a, x) => a + (Number(x.totalAmount) || 0), 0));
      }
      const recurringOut = recent.length ? recent.reduce((a, b) => a + b, 0) / recent.length : 0;

      let balance = startBalance, totalIn = 0, totalOut = 0;
      const labels = [], inflow = [], outflow = [], balanceLine = [];
      for (let i = 1; i <= 6; i++) {
        const span = monthSpan(i, 1);
        const s = span.start, e = span.end;
        const inAmt = this.invoicesRaw
          .filter((x) => x.statusDescription === "Approve" && !this.invPaid(x) && this.inRange(x.dueDate, s, e))
          .reduce((a, x) => a + this.invOutstanding(x), 0);
        const outAmt = this.billsRaw
          .filter((x) => (Number(x.amountDue) || 0) > 0 && this.inRange(x.dueDate, s, e))
          .reduce((a, x) => a + (Number(x.amountDue) || 0), 0) + recurringOut;
        balance += inAmt - outAmt;
        totalIn += inAmt; totalOut += outAmt;
        labels.push(MONTH_LABELS[span.month - 1]);
        inflow.push(Math.round(inAmt));
        outflow.push(Math.round(outAmt));
        balanceLine.push(Math.round(balance));
      }
      return { startBalance, endBalance: balance, totalIn, totalOut, labels, inflow, outflow, balanceLine };
    },
    cashFlowForecastData() {
      const f = this.forecast;
      const ds = (label, data, color) => ({ label, backgroundColor: this.rgba(color, 0.15), borderColor: color, pointBackgroundColor: color, pointBorderColor: "#fff", data });
      return { labels: f.labels, datasets: [ds("Projected balance", f.balanceLine, "#1E3A5F"), ds("Inflows", f.inflow, "#10B981"), ds("Outflows", f.outflow, "#EF4444")] };
    },

    // ---- Tax ----
    taxEvents() {
      return this.taxPeriods
        .filter((p) => p.dueDate)
        .map((p) => ({ id: p.id, name: p.periodCode ? ("Tax period " + p.periodCode) : "Tax filing", dueDate: p.dueDate, amount: Number(p.netTaxPayable) || 0, status: p.statusDescription }))
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 6);
    },
    taxSummary() {
      const estimated = this.taxPeriods
        .filter((p) => p.statusDescription !== "Paid" && p.statusDescription !== "Filed")
        .reduce((s, p) => s + (Number(p.netTaxPayable) || 0), 0);
      return { estimated };
    },
  },

  methods: {
    async fetchAll() {
      this.loading = true;
      const unwrap = (res) => (res && (res.result && res.result.data ? res.result.data : res.result)) || [];
      const results = await Promise.allSettled([
        this.goalApi.getListByCurrentBusiness(),
        this.projectApi.getListByCurrentBusiness(),
        this.loanApi.getListByCurrentBusiness(),
        this.investmentApi.getListByCurrentBusiness(),
        this.budgetApi.getListByCurrentBusiness(),
        this.invoiceApi.getListByCurrentBusiness({ page: 1, pageSize: 1000 }),
        this.billApi.getListByCurrentBusiness({ page: 1, pageSize: 1000 }),
        this.expenseApi.getListByCurrentBusiness({ page: 1, pageSize: 1000 }),
        api.call("get", apiUrl + "TaxPeriods/currentBusiness").then(({ data }) => data).catch(() => ({ result: [] })),
        api.call("get", apiUrl + "banks/currentBusiness").then(({ data }) => data).catch(() => ({ result: [] })),
        this.profileApi.getCurrentProfile(),
      ]);
      const g = (i) => (results[i].status === "fulfilled" ? unwrap(results[i].value) : []);
      this.goals = g(0); this.projects = g(1); this.loans = g(2); this.investments = g(3);
      this.budgets = g(4); this.invoicesRaw = g(5); this.billsRaw = g(6); this.expensesRaw = g(7);
      this.taxPeriods = g(8); this.banks = g(9);
      const pf = results[10];
      if (pf.status === "fulfilled" && pf.value && pf.value.result) {
        this.company = { currency: (pf.value.result.defaultBusiness || {}).currency || "", name: (pf.value.result.defaultBusiness || {}).name || "" };
      }
      const failed = results.slice(0, 5).filter((r) => r.status === "rejected");
      if (failed.length) {
        failed.forEach((r) => console.error("Planner load failed", r.reason));
        this.$toast.warning("Some planner figures couldn't be loaded. Refresh to try again.");
      }
      this.loading = false;
    },

    // ---- aggregation helpers ----
    // start/end are inclusive "yyyy-MM-dd" strings.
    inRange(d, start, end) {
      const s = asDateString(d);
      return !!s && s >= start && s <= end;
    },
    invAmount(x) {
      const g = Number(x.grandTotal) || 0, t = Number(x.totalAmount) || 0;
      if (g > 0) return g;
      if (t > 0) return t;
      return Array.isArray(x.items) ? x.items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0) : 0;
    },
    invPaid(x) {
      return x.isPaid === true || (x.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0) >= this.invAmount(x) - 0.001;
    },
    invOutstanding(x) {
      return Math.max(0, this.invAmount(x) - (x.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0));
    },
    expenseCounted(x) {
      return x.expenseStateDescription === "Approve" || x.expenseStateDescription === "Paid";
    },
    expenseActualForCategory(category, year) {
      const cat = (category || "").trim().toLowerCase();
      return this.expensesRaw
        .filter((x) => this.expenseCounted(x) && asDateString(x.date).substring(0, 4) === String(year))
        .filter((x) => ((x.expenseType || "Other").trim().toLowerCase()) === cat)
        .reduce((s, x) => s + (Number(x.totalAmount) || 0), 0);
    },
    goalProgress(g) {
      const t = Number(g.targetAmount) || 0;
      if (!t) return 0;
      return Math.min(100, Math.round(((Number(g.currentAmount) || 0) / t) * 100));
    },
    pct(a, b) {
      const d = Number(b) || 0;
      return d ? Math.round(((Number(a) || 0) / d) * 100) : 0;
    },

    // ---- formatting ----
    formatCurrency(amount) {
      const v = Number(amount) || 0;
      const cur = this.businessCurrency || "MYR";
      if (cur === "MYR") return v < 0 ? `-RM ${formatMyr(-v)}` : `RM ${formatMyr(v)}`;
      try {
        return new Intl.NumberFormat("en-MY", { style: "currency", currency: cur }).format(v);
      } catch (e) {
        return cur + " " + formatMyr(v);
      }
    },
    formatDate(d) {
      const s = asDateString(d);
      return s && s.substring(0, 4) > "1900" ? formatDate(s) : "—";
    },
    palette(i) { return BRAND[i % BRAND.length]; },
    rgba(hex, a) {
      const h = hex.replace("#", "");
      return `rgba(${parseInt(h.substr(0, 2), 16)}, ${parseInt(h.substr(2, 2), 16)}, ${parseInt(h.substr(4, 2), 16)}, ${a})`;
    },
    daysText(d) {
      const days = diffDays(asDateString(d), mytToday());
      if (days < 0) return Math.abs(days) + "d overdue";
      if (days === 0) return "Due today";
      return "In " + days + "d";
    },
    deadlineColor(d) {
      const days = diffDays(asDateString(d), mytToday());
      if (days < 0) return "danger";
      if (days <= 14) return "warning";
      if (days <= 60) return "info";
      return "secondary";
    },
    loanColor(s) { return { 0: "info", 1: "success", 2: "secondary", 3: "danger" }[s] || "secondary"; },
    statusLabel(entity, val) {
      const maps = {
        project: ["Planning", "In progress", "On track", "At risk", "Completed"],
        loan: ["Active", "Paid off", "Available", "Overdue"],
        goal: ["Active", "Completed", "At risk"],
      };
      return (maps[entity] || [])[val] || "";
    },

    // ---- CRUD ----
    fieldSchema(entity) {
      const yr = this.currentYear;
      const schemas = {
        goal: [
          { key: "name", label: "Goal name", type: "text", full: true },
          { key: "category", label: "Category", type: "select", options: ["Growth", "Savings", "Efficiency", "Capital", "Marketing", "Other"] },
          { key: "status", label: "Status", type: "enum", options: [{ value: 0, label: "Active" }, { value: 1, label: "Completed" }, { value: 2, label: "At risk" }] },
          { key: "targetAmount", label: "Target amount (" + this.businessCurrency + ")", type: "number" },
          { key: "currentAmount", label: "Current amount (" + this.businessCurrency + ")", type: "number" },
          { key: "targetDate", label: "Target date", type: "date" },
          { key: "notes", label: "Notes", type: "textarea", full: true },
        ],
        project: [
          { key: "name", label: "Project name", type: "text", full: true },
          { key: "manager", label: "Manager", type: "text" },
          { key: "status", label: "Status", type: "enum", options: [{ value: 0, label: "Planning" }, { value: 1, label: "In progress" }, { value: 2, label: "On track" }, { value: 3, label: "At risk" }, { value: 4, label: "Completed" }] },
          { key: "budget", label: "Budget (" + this.businessCurrency + ")", type: "number" },
          { key: "spent", label: "Spent (" + this.businessCurrency + ")", type: "number" },
          { key: "startDate", label: "Start date", type: "date" },
          { key: "endDate", label: "End date", type: "date" },
          { key: "progress", label: "Progress (%)", type: "number" },
          { key: "projectedReturn", label: "Projected return (" + this.businessCurrency + ")", type: "number" },
          { key: "notes", label: "Notes", type: "textarea", full: true },
        ],
        loan: [
          { key: "name", label: "Loan name", type: "text", full: true },
          { key: "lender", label: "Lender", type: "text" },
          { key: "loanType", label: "Type", type: "text" },
          { key: "status", label: "Status", type: "enum", options: [{ value: 0, label: "Active" }, { value: 1, label: "Paid off" }, { value: 2, label: "Available" }, { value: 3, label: "Overdue" }] },
          { key: "principal", label: "Principal (" + this.businessCurrency + ")", type: "number" },
          { key: "interestRate", label: "Interest rate (%)", type: "number" },
          { key: "monthlyPayment", label: "Monthly payment (" + this.businessCurrency + ")", type: "number" },
          { key: "amountPaid", label: "Amount paid (" + this.businessCurrency + ")", type: "number" },
          { key: "nextPaymentDate", label: "Next payment", type: "date" },
          { key: "notes", label: "Notes", type: "textarea", full: true },
        ],
        investment: [
          { key: "name", label: "Investment name", type: "text", full: true },
          { key: "investmentType", label: "Type", type: "text" },
          { key: "amount", label: "Amount (" + this.businessCurrency + ")", type: "number" },
          { key: "returnAmount", label: "Return (" + this.businessCurrency + ")", type: "number" },
          { key: "performance", label: "Performance (%)", type: "number" },
          { key: "notes", label: "Notes", type: "textarea", full: true },
        ],
        budget: [
          { key: "category", label: "Category (match expense type)", type: "text", full: true },
          { key: "year", label: "Year", type: "number" },
          { key: "amount", label: "Budget amount (" + this.businessCurrency + ")", type: "number" },
          { key: "notes", label: "Notes", type: "textarea", full: true },
        ],
      };
      return schemas[entity] || [];
    },
    apiFor(entity) {
      return { goal: this.goalApi, project: this.projectApi, loan: this.loanApi, investment: this.investmentApi, budget: this.budgetApi }[entity];
    },
    labelFor(entity) {
      return { goal: "Goal", project: "Project", loan: "Loan", investment: "Investment", budget: "Budget" }[entity];
    },
    openCreate(entity) {
      const defaults = entity === "budget" ? { year: this.currentYear } : {};
      this.modal = { show: true, title: "Add " + this.labelFor(entity), fields: this.fieldSchema(entity), item: defaults, entity };
    },
    openEdit(entity, item) {
      this.modal = { show: true, title: "Edit " + this.labelFor(entity), fields: this.fieldSchema(entity), item: { ...item }, entity };
    },
    onSave(payload) {
      const apiClient = this.apiFor(this.modal.entity);
      const isUpdate = !!payload.id;
      const call = isUpdate ? apiClient.update(payload) : apiClient.create(payload);
      const label = this.labelFor(this.modal.entity);
      call
        .then(() => {
          this.modal.show = false;
          this.$toast.success(label + (isUpdate ? " saved" : " added"));
          this.fetchAll();
        })
        .catch((err) => {
          console.error("Planner save failed", err);
          this.$toast.error(
            `Couldn't save the ${label.toLowerCase()}. ` +
              helper.getErrorMessage(err && err.data, "Check your connection and try again.")
          );
        });
    },
    confirmDelete(entity, item) {
      this.del = { show: true, entity, item, label: this.labelFor(entity).toLowerCase(), deleting: false };
    },
    doDelete() {
      if (this.del.deleting) return;
      const apiClient = this.apiFor(this.del.entity);
      const label = this.labelFor(this.del.entity);
      this.del.deleting = true;
      apiClient
        .delete(this.del.item.id)
        .then(() => {
          this.del.show = false;
          this.$toast.success(label + " deleted");
          this.fetchAll();
        })
        .catch((err) => {
          console.error("Planner delete failed", err);
          this.$toast.error(`Couldn't delete the ${label.toLowerCase()}. Check your connection and try again.`);
        })
        .finally(() => {
          this.del.deleting = false;
        });
    },

    navigateTo(url) {
      if (url && this.$route.path !== url) this.$router.push(url).catch(() => {});
    },
  },
};
</script>

<style scoped>
.pl-header { display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: space-between; margin-bottom: 1.75rem; }
.pl-title { font-size: 1.75rem; font-weight: 800; letter-spacing: -0.02em; color: var(--marketing-primary); margin: 0 0 0.25rem; }
.pl-subtitle { color: var(--marketing-gray-500); margin: 0; }
.refresh-btn { width: 34px; height: 34px; border-radius: 8px; border: 1px solid var(--marketing-gray-300); background: #fff; color: var(--marketing-gray-600); }
.refresh-btn:hover:not(:disabled) { color: var(--marketing-accent); border-color: var(--marketing-accent); }
.refresh-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.kpi-row.loading { opacity: 0.6; pointer-events: none; }
.kpi-card { background: #fff; border: 1px solid var(--marketing-gray-200); border-radius: 14px; padding: 1.25rem 1.3rem; height: 100%; }
.kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
.kpi-label { font-size: 0.85rem; font-weight: 600; color: var(--marketing-gray-500); }
.kpi-chip { width: 40px; height: 40px; border-radius: 11px; display: grid; place-items: center; font-size: 1.05rem; }
.chip-navy { background: rgba(30, 58, 95, 0.10); color: var(--marketing-primary); }
.chip-success { background: rgba(16, 185, 129, 0.13); color: var(--marketing-accent-dark); }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.chip-info { background: rgba(59, 130, 246, 0.12); color: var(--marketing-info); }
.kpi-value { font-size: 1.6rem; font-weight: 800; line-height: 1.05; letter-spacing: -0.02em; color: var(--marketing-primary); }
.kpi-sub { font-size: 0.74rem; color: var(--marketing-gray-400); margin-top: 0.4rem; }

.pl-card { border: 1px solid var(--marketing-gray-200); border-radius: 14px; }
.pl-card-header { background: transparent; border-bottom: 1px solid var(--marketing-gray-100); padding: 1.1rem 1.5rem 0.9rem; display: flex; align-items: center; justify-content: space-between; }
.pl-card-title { font-size: 1.05rem; font-weight: 700; color: var(--marketing-primary); }
.pl-card-footer { background: transparent; border-top: 1px solid var(--marketing-gray-100); }

.btn-brand-accent { background: var(--marketing-accent); border-color: var(--marketing-accent); color: #fff; font-weight: 600; }
.btn-brand-accent:hover { background: var(--marketing-accent-dark); color: #fff; }
.btn-brand-outline { border: 1px solid var(--marketing-primary); color: var(--marketing-primary); background: #fff; font-weight: 600; }
.btn-brand-outline:hover { background: var(--marketing-primary); color: #fff; }

.text-success-strong { color: var(--marketing-accent-dark) !important; }
.text-danger-strong { color: var(--marketing-danger) !important; }

.pl-placeholder { min-height: 180px; display: flex; align-items: center; justify-content: center; }
.pl-empty { text-align: center; padding: 2.5rem 1rem; color: var(--marketing-gray-500); }

.pl-table th { font-size: 0.8rem; color: var(--marketing-gray-500); border-top: none; border-bottom: 1px solid var(--marketing-gray-200); }
.pl-table td { vertical-align: middle; border-top: 1px solid var(--marketing-gray-100); }
.pl-table tfoot .pl-total td { border-top: 2px solid var(--marketing-gray-200); font-weight: 800; color: var(--marketing-gray-700); }
.cat-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 8px; }

.goal-row, .loan-row { padding: 0.75rem 0; border-bottom: 1px solid var(--marketing-gray-100); }
.goal-row:last-child, .loan-row:last-child { border-bottom: none; }
.tax-row { padding: 0.7rem 0; border-bottom: 1px solid var(--marketing-gray-100); }
.tax-row:last-child { border-bottom: none; }

.metric { border-radius: 12px; padding: 1rem 1.1rem; background: var(--marketing-gray-50); border: 1px solid var(--marketing-gray-200); height: 100%; }
.metric-label { font-size: 0.8rem; font-weight: 600; color: var(--marketing-gray-500); margin-bottom: 0.3rem; }
.metric-value { font-size: 1.4rem; font-weight: 800; color: var(--marketing-gray-900); line-height: 1.1; }
.metric-info { background: rgba(59, 130, 246, 0.07); } .metric-info .metric-label { color: var(--marketing-info); }
.metric-success { background: rgba(16, 185, 129, 0.08); } .metric-success .metric-label { color: var(--marketing-accent-dark); }
.metric-navy { background: rgba(30, 58, 95, 0.06); } .metric-navy .metric-label { color: var(--marketing-primary); }

@media (max-width: 575.98px) { .pl-title { font-size: 1.4rem; } .metric { margin-bottom: 1rem; } }
</style>
